/**
 * Where Microsoft sends an organisation's Microsoft 365 admin after they
 * consent to GroundBrain's app (`plans/sharepoint-sync.md`, question 26).
 *
 * The consent grants two things, and the page says both plainly, with what
 * narrows each: mail in every mailbox, read only from folders a person
 * chooses in their own (`plans/outlook-sync.md`, question 2); and every
 * SharePoint site, synced only from folders a person links and can open
 * themselves (`plans/sharepoint-sync.md`, 2026-10-19).
 *
 * **Outside the sign-in** (`App.tsx`): the admin has no GroundBrain account,
 * and needs none. The page reads only what Microsoft put in its address, shows
 * the organisation's id for the admin to pass on, and calls nothing: recording
 * the organisation is the backoffice's write, made by us with the admin.
 */
export const SHAREPOINT_CONSENT_PATH = "/connectors/sharepoint/consent";

export function SharepointConsentPage({ search = window.location.search }: { search?: string }) {
  const params = new URLSearchParams(search);
  const organisation = params.get("tenant");
  const consented = params.get("admin_consent")?.toLowerCase() === "true" && !params.get("error");
  // Entra's description runs to several lines of codes and ids; its first says what happened.
  const why = params.get("error_description")?.split(/\r?\n/)[0]?.trim() || null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-6">
      <main className="w-full max-w-[520px] rounded-card-lg border-[1.5px] border-border bg-surface p-7 text-[0.92rem] text-ink-prose">
        <p className="font-display text-[0.7rem] font-semibold tracking-[0.12em] text-muted uppercase">GroundBrain</p>
        {consented ? (
          <>
            <h1 className="mt-2 text-[1.2rem] font-bold text-ink">Your Microsoft 365 is connected to GroundBrain</h1>
            {organisation && (
              <p className="mt-3">
                Your organisation’s id is <span className="font-mono font-semibold text-ink">{organisation}</span>.
                Send it to the person setting this up with you at GroundBrain.
              </p>
            )}
            <p className="mt-3">
              GroundBrain may now read mail in your organisation’s mailboxes. It reads only the Outlook folders
              each person chooses in GroundBrain, from their own mailbox, once they have confirmed who they are
              with Microsoft.
            </p>
            <p className="mt-3">
              In SharePoint, GroundBrain may now open every site. It syncs only the folders people link to
              projects in GroundBrain, and a person can link only a folder they can open in SharePoint
              themselves.
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-2 text-[1.2rem] font-bold text-ink">The consent didn’t finish</h1>
            <p className="mt-3">{why ?? "Microsoft didn’t say why."}</p>
            <p className="mt-3">Nothing was changed. You can open the link again to try once more.</p>
          </>
        )}
      </main>
    </div>
  );
}
